import proj4 from 'proj4';
import { LatitudeLongitudeBoundingBox } from 'app/unity/ContentViewerTypes/LatitudeLongitudeBoundingBox';
import { VisibleTiles } from 'app/unity/ContentViewerTypes/VisibleTiles';

import { Capabilities, HTTPMethod, HTTPMethodValueType, TileMatrix, TileMatrixSet } from './WMTSTypes';

export type WMTSTileSet = {
    getTileURL?: string;
    layerID?: string;
    styleID?: string;
    formatID?: string;
    tileMatrixSetID?: string;
    tileMatrixSet?: TileMatrixSet;
};

export class WMTSUtils {
    public getArray<T>(collection: T | T[] | undefined): T[] {
        if (Array.isArray(collection)) {
            return collection;
        }

        return collection ? [collection] : [];
    }

    public getFirst<T>(collection: T | T[] | undefined): T | undefined {
        return this.getArray(collection)[0];
    }

    public find<T>(
        collection: T | T[] | undefined,
        predicate: (value: T, index: number, obj: T[]) => boolean,
    ): T | undefined {
        return this.getArray(collection).find(predicate);
    }

    private filter<T>(collection: T | T[] | undefined, predicate: (value: T, index: number, obj: T[]) => boolean): T[] {
        return this.getArray(collection).filter(predicate);
    }

    private isValueType(method: HTTPMethod, valueType: HTTPMethodValueType): boolean {
        const value = method['ows:Constraint']['ows:AllowedValues']['ows:Value'];
        if (Array.isArray(value)) {
            return value.find(x => x === valueType) !== undefined;
        }
        return value === valueType;
    }

    public getPossibleRequests(
        capabilities: Capabilities,
        requestName: string,
        valueType: HTTPMethodValueType = 'KVP',
    ) {
        if (!capabilities) {
            return [];
        }

        const httpMethods = this.filter(
            capabilities['ows:OperationsMetadata']?.['ows:Operation'],
            x => x['@_name'] === requestName,
        )
            .flatMap(x => x['ows:DCP'])
            .flatMap(x => x['ows:HTTP']['ows:Get'] ?? []);

        return this.filter(httpMethods, x => this.isValueType(x, valueType));
    }

    private latLongToTile(latitude: number, longitude: number, tileMatrixSet: TileMatrixSet, tileMatrix: TileMatrix) {
        // TODO(bjarne): Take this top left corner into account? Depends on the
        // eventual map we'll use.
        // const [topLeftCornerX, topLeftCornerY] = tileMatrix.TopLeftCorner.split(' ');
        // parseFloat(topLeftCornerX);
        // parseFloat(topLeftCornerY);

        const mapExtents = new Map<string, { x: number; y: number }>([
            ['urn:ogc:def:crs:epsg::4326', { x: 180, y: 90 }],
            ['urn:ogc:def:crs:epsg:6.18.3:3857', { x: 6378137.0 * Math.PI, y: 6378137.0 * Math.PI }],
            // TODO(bjarne): These coordinates seem slightly off after conversion in
            // at least one of my tests. This might have something to do with the
            // fact that the y scaling for the "full map" would be 20048966.10 whereas
            // the maps get cut off at 85.06 degrees to make it square, producing
            // 20037508.34 (6378137.0 * PI).
            ['urn:ogc:def:crs:epsg::3857', { x: 6378137.0 * Math.PI, y: 6378137.0 * Math.PI }],
        ]);

        const supportedCRS: string = tileMatrixSet['ows:SupportedCRS'].toLowerCase();
        const coordSpaceExtents = mapExtents.get(supportedCRS);
        if (!coordSpaceExtents) {
            throw new Error(`Failed to get map extents for CRS: '${supportedCRS}'`);
        }

        const minCoordSpaceX = -coordSpaceExtents.x;
        const maxCoordSpaceX = coordSpaceExtents.x;
        const minCoordSpaceY = -coordSpaceExtents.y;
        const maxCoordSpaceY = coordSpaceExtents.y;
        const mapCoordSpaceWidth = maxCoordSpaceX - minCoordSpaceX;
        const mapCoordSpaceHeight = maxCoordSpaceY - minCoordSpaceY;

        const converter = proj4('EPSG:4326', supportedCRS);
        const [coordSpaceX, coordSpaceY] = converter.forward([longitude, latitude]);

        // TODO(bjarne): If we don't have maps that span the entire world, we would
        // need to compute the "theoretical" world map count like this. Then we'd
        // use the calculated tiles offset by the TopLeftCorner.
        // const tileCountX = Math.pow(2, zoom + 1);
        // const tileCountY = Math.pow(2, zoom);
        const tileCountX = tileMatrix.MatrixWidth;
        const tileCountY = tileMatrix.MatrixHeight;

        const pixelsX = (coordSpaceX - minCoordSpaceX) / (mapCoordSpaceWidth / tileMatrix.TileWidth / tileCountX);
        const pixelsY = (coordSpaceY - minCoordSpaceY) / (mapCoordSpaceHeight / tileMatrix.TileHeight / tileCountY);
        const tileX = Math.ceil(pixelsX / tileMatrix.TileWidth) - 1;
        let tileY = Math.ceil(pixelsY / tileMatrix.TileHeight) - 1;

        const tileU = (pixelsX - tileX * tileMatrix.TileWidth) / tileMatrix.TileWidth;
        const tileV = (pixelsY - tileY * tileMatrix.TileHeight) / tileMatrix.TileHeight;

        const flipY = true;
        if (flipY) {
            tileY = tileCountY - 1 - tileY;
        }

        return { tileX, tileY, tileU, tileV };
    }

    // NOTE(bjarne): This doesn't take into account any curvature that might
    // happen due to projection.
    public boundingBoxToTiles(
        boundingBox: LatitudeLongitudeBoundingBox,
        tileMatrixSet: TileMatrixSet,
        tileMatrix: TileMatrix,
    ): { tileCountX: number; tileCountY: number; tiles: VisibleTiles[] } {
        boundingBox.min.latitude = Math.max(-85.0, Math.min(boundingBox.min.latitude, 85.0));
        boundingBox.max.latitude = Math.max(-85.0, Math.min(boundingBox.max.latitude, 85.0));
        boundingBox.min.longitude = Math.max(-179.9999, Math.min(boundingBox.min.longitude, 179.9999));
        boundingBox.max.longitude = Math.max(-179.9999, Math.min(boundingBox.max.longitude, 179.9999));

        if (boundingBox.min.latitude > boundingBox.max.latitude) {
            // NOTE(bjarne): We don't support wrap around in Y (latitude) since we don't know
            // what that's supposed to look like.
            const temp = boundingBox.min.latitude;
            boundingBox.min.latitude = boundingBox.max.latitude;
            boundingBox.max.latitude = temp;
        }

        const result00 = this.latLongToTile(
            boundingBox.min.latitude,
            boundingBox.min.longitude,
            tileMatrixSet,
            tileMatrix,
        );
        const result11 = this.latLongToTile(
            boundingBox.max.latitude,
            boundingBox.max.longitude,
            tileMatrixSet,
            tileMatrix,
        );
        if (!(result00 && result11)) {
            return;
        }

        const tileMatrixTileCountX = tileMatrix.MatrixWidth;

        const startTileX = Math.min(result00.tileX, result11.tileX);
        const startTileY = Math.min(result00.tileY, result11.tileY);
        const endTileX = Math.max(result00.tileX, result11.tileX);
        const tileCountY = Math.abs(result11.tileY - result00.tileY) + 1;

        const result: VisibleTiles[] = [];
        let resultTileCountX = 0;
        const resultTileCountY = tileCountY;
        if (boundingBox.min.longitude > boundingBox.max.longitude) {
            // NOTE(bjarne): Wrap around in X.
            result.push(
                new VisibleTiles({
                    topLeftTileX: 0,
                    topLeftTileY: startTileY,
                    tileCountX: startTileX + 1,
                    tileCountY: tileCountY,
                }),
            );
            resultTileCountX = startTileX + 1;

            result.push(
                new VisibleTiles({
                    topLeftTileX: endTileX,
                    topLeftTileY: startTileY,
                    tileCountX: tileMatrixTileCountX - endTileX,
                    tileCountY: tileCountY,
                }),
            );
            resultTileCountX += tileMatrixTileCountX - endTileX;
            if (endTileX == startTileX) {
                --resultTileCountX;
            }
        } else {
            // NOTE(bjarne): Regular bounding box contained within the map.
            const tileCountX = endTileX - startTileX + 1;
            result.push(
                new VisibleTiles({ topLeftTileX: startTileX, topLeftTileY: startTileY, tileCountX, tileCountY }),
            );
            resultTileCountX = tileCountX;
        }

        return { tileCountX: resultTileCountX, tileCountY: resultTileCountY, tiles: result };
    }
}
